/**
 * The admin page: browsing what `worker/visits.js` keeps.
 *
 * Eight tabs over one API. Everything on Overview, Pages, Sources, Audience and
 * Events comes from one `report` request per date range, which is cached here and
 * shared, so switching tabs is instant and costs no queries. Growth, Visitors, one
 * visitor, and Live are their own requests.
 *
 * EVERY STRING FROM THE API IS VISITOR-SUPPLIED — a title, a path, a referrer, a
 * UTM tag. So nothing here uses `innerHTML` with data: `h()` below builds elements
 * and puts every string in as a text node. A page title of `<img onerror>` is shown
 * as those characters.
 *
 * The token is kept in this browser's `localStorage` and sent as a bearer header.
 * Without one, a signed-in admin account's session cookie opens the page instead.
 * `/` is rewritten to `/` by the build, as it is everywhere else.
 */

const API = '/admin/api/';
const TOKEN_KEY = 'immortal-admin-token';
/** The same key `analytics.js` reads. */
const OFF_KEY = 'immortal-analytics-off';

const DAY = 86400000;
const HOUR = 3600000;

// ------------------------------------------------------------------ helpers

/** `h('td', { class: 'num' }, '12')`. Strings become text nodes, never markup. */
function h(tag, attrs = {}, ...children) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs || {})) {
    if (v == null || v === false) continue;
    if (k.startsWith('on')) el.addEventListener(k.slice(2), v);
    else if (k === 'class') el.className = v;
    else el.setAttribute(k, v === true ? '' : String(v));
  }
  for (const child of children.flat(Infinity)) {
    if (child == null || child === false) continue;
    el.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
  return el;
}

const SVG_NS = 'http://www.w3.org/2000/svg';
function s(tag, attrs = {}, ...children) {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) if (v != null) el.setAttribute(k, String(v));
  for (const child of children.flat()) {
    if (child == null) continue;
    el.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
  return el;
}

const nf = new Intl.NumberFormat();
const num = (n) => nf.format(Math.round(Number(n) || 0));
const pct = (n) => `${Math.round((Number(n) || 0) * 100)}%`;

/** `45s`, `3m 12s`, `2h 05m`. */
function dur(ms) {
  // Null is "never measured" — a page closed before it could report — not zero.
  if (ms == null) return '–';
  const t = Math.round((Number(ms) || 0) / 1000);
  if (t < 60) return `${t}s`;
  if (t < 3600) return `${Math.floor(t / 60)}m ${String(t % 60).padStart(2, '0')}s`;
  return `${Math.floor(t / 3600)}h ${String(Math.floor(t / 60) % 60).padStart(2, '0')}m`;
}

const dateTime = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });
const dateOnly = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const timeOnly = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });
const fmtDateTime = (t) => (t ? dateTime.format(new Date(Number(t))) : '–');

/** "3 minutes ago". */
function ago(t) {
  const d = Date.now() - Number(t);
  if (d < 60000) return 'just now';
  if (d < HOUR) return `${Math.floor(d / 60000)}m ago`;
  if (d < DAY) return `${Math.floor(d / HOUR)}h ago`;
  return `${Math.floor(d / DAY)}d ago`;
}

let regionNames = null;
try { regionNames = new Intl.DisplayNames(undefined, { type: 'region' }); } catch { /* old browser */ }
let languageNames = null;
try { languageNames = new Intl.DisplayNames(undefined, { type: 'language' }); } catch { /* old browser */ }

function flag(code) {
  if (!/^[A-Z]{2}$/.test(code) || code === 'XX') return '';
  return String.fromCodePoint(...[...code].map((c) => 0x1f1a5 + c.charCodeAt(0)));
}
function countryName(code) {
  if (!code || code === 'XX') return 'Unknown';
  try { return `${flag(code)} ${regionNames?.of(code) ?? code}`; } catch { return code; }
}
function languageName(code) {
  if (!code) return 'Unknown';
  try { return `${languageNames?.of(code) ?? code} (${code})`; } catch { return code; }
}
const shortId = (id) => String(id).slice(0, 8);

// ------------------------------------------------------------------ the API

function token() {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
}

class Unauthorized extends Error {}

async function api(name, params = {}, { method = 'GET' } = {}) {
  const query = new URLSearchParams(params).toString();
  const headers = token() ? { Authorization: `Bearer ${token()}` } : {};
  // Sent on a write even though the arguments travel in the query string and the
  // body is empty, because the Worker requires it: a cross-site form can make the
  // browser POST here with the admin's cookie attached, but it cannot make it send
  // this header without a preflight the Worker never answers. See `postAllowed`.
  if (method !== 'GET') headers['Content-Type'] = 'application/json';
  const response = await fetch(`${API}${name}${query ? `?${query}` : ''}`, {
    method,
    // No token: the session cookie of a signed-in admin account is what is sent.
    headers,
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (response.status === 401) throw new Unauthorized('wrong token');
  let body = null;
  try { body = await response.json(); } catch { /* reported below */ }
  if (!response.ok) throw new Error(body?.error || `the API said ${response.status}`);
  return body;
}

// ------------------------------------------------------------------ state

const RANGES = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7 days', days: 7 },
  { id: '30d', label: '30 days', days: 30 },
  { id: '90d', label: '90 days', days: 90 },
  { id: '365d', label: '12 months', days: 365 },
  { id: 'all', label: 'All' },
];

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'growth', label: 'Growth' },
  { id: 'pages', label: 'Pages' },
  { id: 'sources', label: 'Sources' },
  { id: 'audience', label: 'Audience' },
  { id: 'events', label: 'Events' },
  { id: 'visitors', label: 'Visitors' },
  { id: 'live', label: 'Live' },
];

const state = {
  range: (() => { try { return localStorage.getItem('immortal-admin-range') || '7d'; } catch { return '7d'; } })(),
  report: null,
  reportKey: '',
  visitorsPage: 0,
};

/** The window a range means, aligned to the start of a local day. */
function window_() {
  const now = Date.now();
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  const range = RANGES.find((r) => r.id === state.range) ?? RANGES[1];
  let from = 0;
  if (range.id === 'today') from = midnight.getTime();
  else if (range.days) from = midnight.getTime() - (range.days - 1) * DAY;
  return { from, to: now, offset: -new Date().getTimezoneOffset() };
}

async function getReport(force = false) {
  const w = window_();
  const key = state.range;
  if (!force && state.report && state.reportKey === key) return state.report;
  const report = await api('report', w);
  state.report = report;
  state.reportKey = key;
  return report;
}

// ------------------------------------------------------------------ pieces

function tile(label, value, hint) {
  return h('div', { class: 'card tile' },
    h('div', { class: 'label' }, label),
    h('div', { class: 'value' }, value),
    hint ? h('div', { class: 'hint' }, hint) : null);
}

function panel(title, aside, ...body) {
  return h('section', { class: 'card panel' },
    h('h2', {}, title, aside ? h('span', { class: 'muted' }, aside) : null),
    ...body);
}

const empty = (text = 'Nothing in this range yet.') => h('div', { class: 'empty' }, text);

/**
 * A sortable table.
 *
 * columns: { key, label, num?, format?(row), render?(row), bar? } — `bar: true` puts
 * a proportional fill behind that cell, scaled to the column `barOf` names.
 */
function table(columns, rows, { sort, limit, onRow, barOf } = {}) {
  if (!rows?.length) return empty();
  let sortKey = sort ?? null;
  let dir = -1;
  const wrap = h('div', { class: 'table-wrap' });

  const draw = () => {
    const sorted = sortKey
      ? [...rows].sort((a, b) => {
        const x = a[sortKey]; const y = b[sortKey];
        if (typeof x === 'number' || typeof y === 'number') return ((Number(x) || 0) - (Number(y) || 0)) * dir;
        return String(x ?? '').localeCompare(String(y ?? '')) * dir;
      })
      : rows;
    const shown = limit ? sorted.slice(0, limit) : sorted;
    const top = barOf ? Math.max(1, ...rows.map((r) => Number(r[barOf]) || 0)) : 1;
    const head = h('tr', {}, columns.map((c) => h('th', {
      class: c.num ? 'num' : null,
      'data-sort': c.key,
      'aria-sort': sortKey === c.key ? (dir < 0 ? 'descending' : 'ascending') : null,
      scope: 'col',
      onclick: () => {
        if (sortKey === c.key) dir = -dir;
        else { sortKey = c.key; dir = c.num ? -1 : 1; }
        draw();
      },
    }, c.label)));
    const body = shown.map((row) => h('tr', {
      class: onRow ? 'clickable' : null,
      onclick: onRow ? () => onRow(row) : null,
      tabindex: onRow ? 0 : null,
      onkeydown: onRow ? (e) => { if (e.key === 'Enter') onRow(row); } : null,
    }, columns.map((c) => {
      const content = c.render ? c.render(row) : c.format ? c.format(row[c.key], row) : row[c.key];
      if (c.bar) {
        const width = Math.max(0, Math.min(100, ((Number(row[barOf]) || 0) / top) * 100));
        return h('td', { class: 'label-cell cell-bar' },
          h('span', { class: 'fill', style: `width:${width}%` }),
          h('span', { class: 'txt' }, content));
      }
      return h('td', { class: c.num ? 'num' : null }, content);
    })));
    wrap.replaceChildren(h('table', {}, h('thead', {}, head), h('tbody', {}, body)));
  };
  draw();
  return wrap;
}

/** A label-plus-count breakdown: the Audience tab and the Overview's side panels. */
function breakdown(rows, { label = (k) => k || 'Unknown', limit } = {}) {
  return table([
    { key: 'k', label: 'Value', bar: true, format: (k) => label(k) },
    { key: 'visitors', label: 'Visitors', num: true, format: num },
    { key: 'views', label: 'Views', num: true, format: num },
  ], rows, { limit, barOf: 'visitors' });
}

// ------------------------------------------------------------------ the chart

const tooltip = document.getElementById('tooltip');
function showTooltip(x, y, title, rows) {
  tooltip.replaceChildren(
    h('div', { class: 't' }, title),
    ...rows.map(([k, v]) => h('div', { class: 'r' }, h('span', {}, k), h('strong', {}, v))),
  );
  tooltip.hidden = false;
  const r = tooltip.getBoundingClientRect();
  const left = Math.min(innerWidth - r.width - 8, x + 14);
  const top = Math.max(8, y - r.height - 12);
  tooltip.style.left = `${Math.max(8, left)}px`;
  tooltip.style.top = `${top}px`;
}
const hideTooltip = () => { tooltip.hidden = true; };

/** Every bucket from the start of the range to now, including the empty ones. */
function filledSeries(report) {
  const { bucket } = report;
  const offset = -new Date().getTimezoneOffset() * 60000;
  const byT = new Map(report.series.map((p) => [Number(p.t), p]));
  let start = report.from || (report.series[0] ? Number(report.series[0].t) : report.to);
  start = Math.floor((start + offset) / bucket) * bucket - offset;
  const out = [];
  for (let t = start; t < report.to && out.length < 1000; t += bucket) {
    // DST moves local midnight by an hour; take whichever point is nearest.
    const p = byT.get(t) ?? byT.get(t + HOUR) ?? byT.get(t - HOUR);
    out.push({ t, views: Number(p?.views) || 0, visitors: Number(p?.visitors) || 0, sessions: Number(p?.sessions) || 0 });
  }
  return out;
}

/**
 * Page views as bars, visitors as a line, one count axis — both are counts of the
 * same kind of thing, so one scale is honest. Hover anywhere for the bucket.
 */
function trafficChart(report) {
  const points = filledSeries(report);
  const holder = h('div', { class: 'chart' });
  const legend = h('div', { class: 'legend' },
    h('span', {}, h('i', { class: 'l1' }), 'Page views'),
    h('span', {}, h('i', { class: 'l2' }), 'Visitors'));
  if (!points.length) return h('div', {}, legend, empty());

  const hourly = report.bucket < DAY;
  const label = (t) => (hourly ? timeOnly.format(new Date(t)) : dateOnly.format(new Date(t)));

  const draw = () => {
    const W = Math.max(280, holder.clientWidth || 800);
    const H = W < 640 ? 200 : 260;
    const pad = { l: 40, r: 8, t: 8, b: 24 };
    const iw = W - pad.l - pad.r;
    const ih = H - pad.t - pad.b;
    const max = Math.max(1, ...points.map((p) => Math.max(p.views, p.visitors)));
    const step = niceStep(max);
    const top = Math.ceil(max / step) * step;
    const x = (i) => pad.l + (i + 0.5) * (iw / points.length);
    const y = (v) => pad.t + ih - (v / top) * ih;
    const bw = Math.max(1, Math.min(28, iw / points.length - 2));

    const svg = s('svg', { viewBox: `0 0 ${W} ${H}`, role: 'img', 'aria-label': 'Page views and visitors over time' });
    const grid = s('g', { class: 'axis' });
    for (let v = 0; v <= top; v += step) {
      grid.append(s('line', { class: 'gridline', x1: pad.l, x2: W - pad.r, y1: y(v), y2: y(v) }));
      grid.append(s('text', { x: pad.l - 6, y: y(v) + 4, 'text-anchor': 'end' }, num(v)));
    }
    const every = Math.ceil(points.length / Math.max(2, Math.floor(iw / 70)));
    points.forEach((p, i) => {
      if (i % every === 0) grid.append(s('text', { x: x(i), y: H - 6, 'text-anchor': 'middle' }, label(p.t)));
    });
    svg.append(grid);

    const bars = s('g');
    points.forEach((p, i) => {
      if (!p.views) return;
      const bh = Math.max(1, ih - (y(p.views) - pad.t));
      const top4 = Math.min(4, bw / 2, bh);
      const x0 = x(i) - bw / 2;
      const y0 = y(p.views);
      // Rounded at the data end only, square at the baseline.
      bars.append(s('path', {
        class: 'bar',
        d: `M${x0},${y0 + bh} V${y0 + top4} Q${x0},${y0} ${x0 + top4},${y0} H${x0 + bw - top4} Q${x0 + bw},${y0} ${x0 + bw},${y0 + top4} V${y0 + bh} Z`,
      }));
    });
    svg.append(bars);
    svg.append(s('path', {
      class: 'line',
      d: points.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p.visitors)}`).join(' '),
    }));
    if (points.length <= 60) {
      const markers = s('g');
      points.forEach((p, i) => markers.append(s('circle', { class: 'marker', cx: x(i), cy: y(p.visitors), r: 3 })));
      svg.append(markers);
    }

    const cross = s('line', { class: 'crosshair', y1: pad.t, y2: pad.t + ih, visibility: 'hidden' });
    svg.append(cross);
    const hit = s('rect', { x: pad.l, y: 0, width: iw, height: H, fill: 'transparent' });
    hit.addEventListener('pointermove', (e) => {
      const rect = svg.getBoundingClientRect();
      const px = ((e.clientX - rect.left) / rect.width) * W;
      const i = Math.max(0, Math.min(points.length - 1, Math.floor(((px - pad.l) / iw) * points.length)));
      const p = points[i];
      cross.setAttribute('x1', x(i));
      cross.setAttribute('x2', x(i));
      cross.setAttribute('visibility', 'visible');
      const title = hourly ? `${dateOnly.format(new Date(p.t))}, ${timeOnly.format(new Date(p.t))}` : dateOnly.format(new Date(p.t));
      showTooltip(e.clientX, e.clientY, title, [['Page views', num(p.views)], ['Visitors', num(p.visitors)], ['Sessions', num(p.sessions)]]);
    });
    hit.addEventListener('pointerleave', () => { cross.setAttribute('visibility', 'hidden'); hideTooltip(); });
    svg.append(hit);
    holder.replaceChildren(svg);
  };

  new ResizeObserver(() => draw()).observe(holder);
  return h('div', {}, legend, holder);
}

function niceStep(max) {
  const raw = max / 4;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const n = raw / mag;
  return Math.max(1, (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * mag);
}

// ------------------------------------------------------------------ the tabs

function pageLabel(row) {
  return [row.title || row.path, row.title ? h('span', { class: 'sub mono' }, row.path) : null];
}

function referrerLabel(k) {
  return k === 'direct' ? 'Direct / none' : k;
}

async function overview() {
  const [r, users] = await Promise.all([
    getReport(),
    // Its own database; a failure there should not take the traffic numbers with it.
    api('users', { from: window_().from }).catch(() => null),
  ]);
  const t = r.totals;
  return [
    h('div', { class: 'tiles' },
      tile('Registered users', users ? num(users.registered) : '–', users
        ? `${num(users.new_in_range)} new · ${num(users.lichess)} via Lichess · ${num(users.unconfirmed)} unconfirmed`
        : 'could not be read'),
      tile('Visitors', num(t.visitors), `${num(t.new_visitors)} new`),
      tile('Sessions', num(t.sessions)),
      tile('Page views', num(t.views)),
      tile('Pages per session', (Number(t.pages_per_session) || 0).toFixed(1)),
      tile('Time on page', dur(t.avg_ms), 'average, visible & active'),
      tile('Session length', dur(t.session_ms), 'average'),
      tile('Bounce rate', pct(t.bounce_rate), 'sessions with one page'),
    ),
    panel('Traffic', r.bucket < DAY ? 'by hour' : 'by day', trafficChart(r)),
    h('div', { class: 'grid-2' },
      panel('Top pages', 'by views', table([
        { key: 'path', label: 'Page', bar: true, render: pageLabel },
        { key: 'visitors', label: 'Visitors', num: true, format: num },
        { key: 'views', label: 'Views', num: true, format: num },
        { key: 'avg_ms', label: 'Time', num: true, format: dur },
      ], r.pages, { limit: 10, barOf: 'views' })),
      panel('Top sources', 'excluding internal links', breakdown(r.referrers, { label: referrerLabel, limit: 10 })),
      panel('Countries', null, breakdown(r.dimensions.country, { label: countryName, limit: 10 })),
      panel('Devices', null, breakdown(r.dimensions.device, { limit: 10 })),
    ),
  ];
}

/**
 * Growth: the numbers `chess-app-phased-plan.md` launches against.
 *
 * Return rates are a share of the visitors whose window has finished, not of
 * everybody in the range — see `growth` in `worker/visits.js` — so the hint under
 * each one says how many that was. A rate over three people is not a rate.
 */
const FUNNEL_LABELS = {
  'import-pgn': 'Imported a PGN file',
  'import-user': 'Imported games by username',
  'review-open': 'Opened a game review',
  'share-card': 'Shared a moment',
  'daily-open': 'Opened the daily game',
  'daily-done': 'Finished the daily game',
  'mine-open': 'Opened puzzles from their games',
  'mine-found': 'Solved a puzzle from their games',
};

const rate = (n, of) => (of ? pct(n / of) : '–');

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Whether our own lobby is worth building yet (Phase 3c): `crowd` in
 * `worker/visits.js`. A grid of the week, each hour shaded by the busiest
 * moment it had in its QUIETEST recent week — an hour counts only when it was
 * busy every week.
 */
function crowdPanel(c) {
  const { atOnce, weeks, hours } = c.lobby;
  const top = Math.max(atOnce, ...c.hours.map((x) => x.low));
  const cells = [];
  cells.push(h('span', {}), ...Array.from({ length: 24 }, (_, i) => h('span', { class: 'crowd-h' }, i % 3 ? '' : String(i))));
  for (let d = 0; d < 7; d++) {
    cells.push(h('span', { class: 'crowd-d' }, DAYS[d]));
    for (let i = 0; i < 24; i++) {
      const x = c.hours[d * 24 + i];
      const share = x.low / top;
      cells.push(h('span', {
        class: `crowd-c${x.low >= atOnce ? ' crowd-ok' : ''}`,
        style: `--v:${share.toFixed(3)}`,
        title: `${DAYS[d]} ${String(i).padStart(2, '0')}:00 — at most ${x.weeks.join(', ')} at once (this week first)`,
      }));
    }
  }
  return panel('A lobby of our own', `build it at ${atOnce}+ people at once, at the same hours, ${weeks} weeks running`,
    h('div', { class: 'tiles' },
      tile('Online now', num(c.now), `${num(c.playing.now)} of them playing`),
      tile(`Busiest moment · ${weeks} weeks`, num(c.peak.n), c.peak.at ? dateTime.format(new Date(c.peak.at)) : 'nobody yet'),
      tile('Regular busy hours', `${num(c.regular)} of ${hours}`, c.cut ? 'Too many views to read them all: the oldest are left out'
        : c.ready ? 'Ready: build the lobby' : 'Until then, strangers are played on Lichess'),
    ),
    h('div', { class: 'crowd-grid', role: 'img', 'aria-label': 'People online at once, by hour of the week' }, cells));
}

async function growth() {
  const [g, crowd] = await Promise.all([
    api('growth', window_()),
    api('crowd', { offset: -new Date().getTimezoneOffset() }).catch(() => null),
  ]);
  const r = g.retention;
  const perVisitor = g.visitors ? (g.imports.games / g.visitors).toFixed(2) : '–';
  const funnel = Object.entries(g.events).map(([event, e]) => ({
    event,
    n: e.n,
    visitors: e.visitors,
    share: g.visitors ? e.visitors / g.visitors : 0,
  }));
  return [
    h('div', { class: 'tiles' },
      tile('New visitors', num(r.cohort), 'first seen in this range'),
      tile('Came back on day 1', rate(r.d1, r.d1_eligible), `${num(r.d1)} of ${num(r.d1_eligible)} · 24–48h after their first visit`),
      tile('Came back on day 7', rate(r.d7, r.d7_eligible), `${num(r.d7)} of ${num(r.d7_eligible)} · 7–8 days after`),
      tile('Came back within a week', rate(r.w1, r.d7_eligible), `${num(r.w1)} of ${num(r.d7_eligible)}`),
      tile('Games imported per visitor', perVisitor, `${num(g.imports.games)} games · ${num(g.imports.visitors)} importers`),
      tile('Daily-game streaks', g.streaks.players ? (Math.round(g.streaks.avg * 10) / 10).toString() : '–',
        g.streaks.players ? `average · longest ${num(g.streaks.max)} · ${num(g.streaks.players)} players` : 'not launched yet'),
    ),
    panel('Weekly cohorts', 'by the week of the first visit', table([
      { key: 't', label: 'Week of', format: (t) => dateOnly.format(new Date(Number(t))) },
      { key: 'cohort', label: 'New', num: true, format: num },
      { key: 'd1', label: 'Day 1', num: true, format: (v, row) => rate(v, row.d1_eligible) },
      { key: 'd7', label: 'Day 7', num: true, format: (v, row) => rate(v, row.d7_eligible) },
      { key: 'w1', label: 'Within a week', num: true, format: (v, row) => rate(v, row.d7_eligible) },
    ], g.cohorts, { sort: 't' })),
    h('div', { class: 'grid-2' },
      panel('What visitors did', `of ${num(g.visitors)} visitors · zero until the feature ships`, table([
        { key: 'event', label: 'Action', bar: true, format: (k) => FUNNEL_LABELS[k] ?? k },
        { key: 'visitors', label: 'Visitors', num: true, format: num },
        { key: 'share', label: 'Share', num: true, format: pct },
        { key: 'n', label: 'Times', num: true, format: num },
      ], funnel, { barOf: 'visitors' })),
      panel('Arrivals from share cards', 'links tagged utm_medium=share', g.shares.length
        ? breakdown(g.shares, { label: (k) => k || '(untagged kind)' })
        : empty('No arrivals from a share card yet.')),
    ),
    crowd ? crowdPanel(crowd) : null,
  ];
}

async function pages() {
  const r = await getReport();
  const entries = new Map(r.entries.map((e) => [e.path, e]));
  const rows = r.pages.map((p) => ({
    ...p,
    entries: Number(entries.get(p.path)?.entries) || 0,
    exits: Number(entries.get(p.path)?.exits) || 0,
  }));
  return [
    panel('Pages', `${rows.length} in this range`, table([
      { key: 'path', label: 'Page', bar: true, render: pageLabel },
      { key: 'views', label: 'Views', num: true, format: num },
      { key: 'visitors', label: 'Visitors', num: true, format: num },
      { key: 'avg_ms', label: 'Avg. time', num: true, format: dur },
      { key: 'scroll', label: 'Scroll', num: true, format: (v) => `${Math.round(v || 0)}%` },
      { key: 'entries', label: 'Entries', num: true, format: num },
      { key: 'exits', label: 'Exits', num: true, format: num },
    ], rows, { sort: 'views', barOf: 'views' })),
  ];
}

async function sources() {
  const r = await getReport();
  return [
    h('div', { class: 'grid-2' },
      panel('Referrers', 'the site that linked here', breakdown(r.referrers, { label: referrerLabel })),
      panel('Campaigns', 'utm_ tags · Google Ads clicks count as google / cpc', table([
        { key: 'source', label: 'Source', bar: true, render: (row) => [row.source || '–', h('span', { class: 'sub' }, [row.medium, row.campaign].filter(Boolean).join(' · '))] },
        { key: 'visitors', label: 'Visitors', num: true, format: num },
        { key: 'views', label: 'Views', num: true, format: num },
      ], r.campaigns, { barOf: 'visitors' })),
    ),
  ];
}

async function audience() {
  const r = await getReport();
  const d = r.dimensions;
  return [
    h('div', { class: 'grid-3' },
      panel('Countries', null, breakdown(d.country, { label: countryName })),
      panel('Languages', null, breakdown(d.lang, { label: languageName })),
      panel('Devices', null, breakdown(d.device)),
      panel('Browsers', null, breakdown(d.browser)),
      panel('Operating systems', null, breakdown(d.os)),
      panel('Screen sizes', 'CSS pixels', breakdown(d.screen)),
      panel('Timezones', null, breakdown(d.tz)),
      panel('Installed app', null, breakdown(d.mode, { label: (k) => (k === 'app' ? 'Installed app' : k === 'browser' ? 'Browser tab' : k) })),
      panel('Site sections', null, breakdown(d.section)),
    ),
  ];
}

async function events() {
  const r = await getReport();
  const names = [...new Set(r.events.map((e) => e.event))].sort();
  const holder = h('div');
  const select = h('select', { 'aria-label': 'Event' },
    h('option', { value: '' }, 'All events'),
    names.map((n) => h('option', { value: n }, n)));
  const draw = () => {
    const rows = select.value ? r.events.filter((e) => e.event === select.value) : r.events;
    holder.replaceChildren(table([
      { key: 'event', label: 'Event', bar: true, render: (row) => [row.event, row.detail ? h('span', { class: 'sub' }, row.detail) : null] },
      { key: 'n', label: 'Count', num: true, format: num },
      { key: 'visitors', label: 'Visitors', num: true, format: num },
      { key: 'avg_value', label: 'Avg. value', num: true, format: (v) => (v ? (Math.round(v * 10) / 10).toString() : '–') },
    ], rows, { sort: 'n', barOf: 'n' }));
  };
  select.addEventListener('change', draw);
  draw();
  // Totals per event name, for the eye before the detail.
  const totals = names.map((n) => ({
    k: n,
    views: r.events.filter((e) => e.event === n).reduce((a, e) => a + Number(e.n), 0),
    visitors: Math.max(...r.events.filter((e) => e.event === n).map((e) => Number(e.visitors))),
  }));
  return [
    h('div', { class: 'grid-2' },
      panel('Events', 'what people did on the pages', table([
        { key: 'k', label: 'Event', bar: true },
        { key: 'views', label: 'Count', num: true, format: num },
      ], totals, { sort: 'views', barOf: 'views' })),
      panel('By detail', null, h('div', { class: 'row', style: 'margin-bottom:8px' }, select), holder),
    ),
  ];
}

async function visitors() {
  const w = window_();
  const data = await api('visitors', { ...w, page: state.visitorsPage });
  const pagesTotal = Math.max(1, Math.ceil(data.total / data.page_size));
  const pager = h('div', { class: 'pager' },
    h('span', { class: 'muted' }, `Page ${state.visitorsPage + 1} of ${pagesTotal}`),
    h('button', {
      type: 'button',
      disabled: state.visitorsPage === 0,
      onclick: () => { state.visitorsPage--; render(); },
    }, 'Previous'),
    h('button', {
      type: 'button',
      disabled: state.visitorsPage + 1 >= pagesTotal,
      onclick: () => { state.visitorsPage++; render(); },
    }, 'Next'));
  return [
    panel('Visitors', `${num(data.total)} in this range · anonymous ids`, table([
      { key: 'visitor', label: 'Visitor', render: (row) => h('span', { class: 'mono' }, shortId(row.visitor)) },
      { key: 'last', label: 'Last seen', num: true, format: (v) => ago(v) },
      { key: 'first', label: 'First seen', num: true, format: (v) => dateOnly.format(new Date(Number(v))) },
      { key: 'sessions', label: 'Sessions', num: true, format: num },
      { key: 'views', label: 'Views', num: true, format: num },
      { key: 'ms', label: 'Time', num: true, format: dur },
      { key: 'country', label: 'Country', format: countryName },
      { key: 'device', label: 'Device', render: (row) => `${row.device} · ${row.browser} · ${row.os}` },
    ], data.rows, { onRow: (row) => { location.hash = `#visitor/${row.visitor}`; } }), pager),
  ];
}

async function visitorDetail(id) {
  const data = await api('visitor', { id });
  const back = h('a', { href: '#visitors', class: 'back' }, '← Visitors');
  if (!data.views.length) return [back, panel('Visitor', null, empty('No page views from this id.'))];

  const views = [...data.views].reverse();
  const latest = data.views[0];
  const sessions = new Map();
  for (const v of views) {
    if (!sessions.has(v.session)) sessions.set(v.session, { id: v.session, views: [], events: [] });
    sessions.get(v.session).views.push(v);
  }
  for (const e of data.events) sessions.get(e.session)?.events.push(e);

  const totalMs = views.reduce((a, v) => a + Number(v.ms), 0);
  const facts = [
    ['Visitor id', h('span', { class: 'mono' }, data.id)],
    ['First seen', fmtDateTime(views[0].ts)],
    ['Last seen', `${fmtDateTime(latest.last)} (${ago(latest.last)})`],
    ['Sessions', num(sessions.size)],
    ['Page views', num(views.length)],
    ['Time on site', dur(totalMs)],
    ['Country', countryName(latest.country)],
    ['Device', `${latest.device} · ${latest.mode === 'app' ? 'installed app' : 'browser'}`],
    ['Browser', `${latest.browser} on ${latest.os}`],
    ['Language', languageName(latest.lang)],
    ['Timezone', latest.tz || '–'],
    ['Screen', latest.screen || '–'],
  ];

  const sessionCards = [...sessions.values()].reverse().map((sess) => {
    const first = sess.views[0];
    const last = sess.views[sess.views.length - 1];
    const ms = sess.views.reduce((a, v) => a + Number(v.ms), 0);
    const items = [
      ...sess.views.map((v) => ({ t: Number(v.ts), v })),
      ...sess.events.map((e) => ({ t: Number(e.ts), e })),
    ].sort((a, b) => a.t - b.t);
    const source = first.utm_source
      ? `${first.utm_source}${first.utm_medium ? ` / ${first.utm_medium}` : ''}${first.utm_campaign ? ` · ${first.utm_campaign}` : ''}`
      : referrerLabel(first.referrer);
    return h('section', { class: 'card session' },
      h('header', {},
        h('strong', {}, fmtDateTime(first.ts)),
        h('span', { class: 'muted' },
          `${sess.views.length} page${sess.views.length === 1 ? '' : 's'} · ${dur(ms)} active · ${dur(Number(last.last) - Number(first.ts))} span · from ${source}`)),
      h('ol', { class: 'timeline' }, items.map((item) => (item.v
        ? h('li', {},
          h('span', { class: 'when' }, timeOnly.format(new Date(item.t))),
          item.v.title || item.v.path,
          h('span', { class: 'chip' }, dur(item.v.ms)),
          item.v.scroll < 100 ? h('span', { class: 'chip' }, `${item.v.scroll}% scrolled`) : null,
          h('span', { class: 'sub mono' }, item.v.path))
        : h('li', { class: 'event' },
          h('span', { class: 'when' }, timeOnly.format(new Date(item.t))),
          h('strong', {}, item.e.event),
          item.e.detail ? ` ${item.e.detail}` : '',
          Number(item.e.value) ? h('span', { class: 'chip' }, `value ${item.e.value}`) : null)))));
  });

  return [
    back,
    panel('Visitor', `seen ${ago(latest.last)}`,
      h('dl', { class: 'facts' }, facts.map(([k, v]) => h('div', {}, h('dt', {}, k), h('dd', {}, v))))),
    ...sessionCards,
  ];
}

async function live() {
  const data = await api('live');
  setLive(data.visitors_now);
  return [
    h('div', { class: 'tiles' },
      tile('Visitors now', num(data.visitors_now), 'a page open in the last 5 minutes')),
    h('div', { class: 'grid-2' },
      panel('Open right now', null, table([
        { key: 'path', label: 'Page', bar: true },
        { key: 'visitors', label: 'Visitors', num: true, format: num },
      ], data.active, { barOf: 'visitors' })),
      panel('Latest page loads', 'refreshes every 15 seconds', table([
        { key: 'ts', label: 'When', format: (v) => ago(v) },
        { key: 'path', label: 'Page', render: pageLabel },
        { key: 'visitor', label: 'Visitor', render: (row) => h('span', { class: 'mono' }, shortId(row.visitor)) },
        { key: 'country', label: 'From', format: (v, row) => `${countryName(v)} · ${row.device}` },
      ], data.recent, { onRow: (row) => { location.hash = `#visitor/${row.visitor}`; } })),
    ),
  ];
}

const VIEWS = { overview, growth, pages, sources, audience, events, visitors, live };

// ------------------------------------------------------------------ rendering

const main = document.getElementById('main');
let renderSeq = 0;
let liveTimer = null;

function route() {
  const hash = location.hash.slice(1);
  const visitor = /^visitor\/([0-9a-f-]{36})$/.exec(hash);
  if (visitor) return { tab: 'visitors', visitor: visitor[1] };
  return { tab: VIEWS[hash] ? hash : 'overview' };
}

async function render({ quiet = false } = {}) {
  const seq = ++renderSeq;
  const { tab, visitor } = route();
  for (const a of document.querySelectorAll('#tabs a')) {
    a.setAttribute('aria-current', a.dataset.tab === tab ? 'page' : 'false');
  }
  clearInterval(liveTimer);
  if (tab === 'live') liveTimer = setInterval(() => render({ quiet: true }), 15000);
  if (!quiet) main.replaceChildren(h('div', { class: 'loading' }, 'Loading…'));
  hideTooltip();
  try {
    const content = visitor ? await visitorDetail(visitor) : await VIEWS[tab]();
    if (seq !== renderSeq) return;
    main.replaceChildren(...content.filter(Boolean));
  } catch (err) {
    if (seq !== renderSeq) return;
    if (err instanceof Unauthorized) return signOut(token() ? 'That token was not accepted.' : '');
    main.replaceChildren(h('div', { class: 'card panel' },
      h('h2', {}, 'Could not load this'),
      h('p', { class: 'error' }, err.message)));
  }
}

function setLive(n) {
  document.getElementById('live-count').textContent = num(n);
  document.getElementById('live-pill').classList.toggle('on', n > 0);
}

async function pollLive() {
  try { setLive((await api('live')).visitors_now); } catch { /* the pill just keeps its number */ }
}

function drawChrome() {
  const ranges = document.getElementById('ranges');
  ranges.replaceChildren(...RANGES.map((r) => h('button', {
    type: 'button',
    'aria-pressed': String(r.id === state.range),
    onclick: () => {
      state.range = r.id;
      state.visitorsPage = 0;
      try { localStorage.setItem('immortal-admin-range', r.id); } catch { /* fine */ }
      drawChrome();
      render();
    },
  }, r.label)));
  document.getElementById('tabs').replaceChildren(...TABS.map((t) => h('a', {
    href: `#${t.id}`, 'data-tab': t.id,
  }, t.label)));
}

// ------------------------------------------------------------------ signing in

function signOut(message = '') {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* fine */ }
  state.report = null;
  clearInterval(liveTimer);
  document.getElementById('app').hidden = true;
  document.getElementById('signin').hidden = false;
  const error = document.getElementById('signin-error');
  error.textContent = message;
  error.hidden = !message;
  document.getElementById('token').focus();
}

async function start() {
  // Signed in with an admin account rather than a token, Sign out is the account
  // page's to do: forgetting a token that was never given would change nothing.
  document.getElementById('signout').textContent = token() ? 'Sign out' : 'Account';
  document.getElementById('signin').hidden = true;
  document.getElementById('app').hidden = false;
  drawChrome();
  await render();
  pollLive();
}

document.getElementById('signin-form').addEventListener('submit', (e) => {
  e.preventDefault();
  const value = document.getElementById('token').value.trim();
  try { localStorage.setItem(TOKEN_KEY, value); } catch { /* the session still works until reload */ }
  start();
});

addEventListener('hashchange', () => render());
document.getElementById('refresh').addEventListener('click', () => {
  state.report = null;
  render();
  pollLive();
});
setInterval(() => { if (!document.hidden && !document.getElementById('app').hidden) pollLive(); }, 60000);

// ------------------------------------------------------------------ settings

const settings = document.getElementById('settings');
const optOut = document.getElementById('opt-out');
document.getElementById('settings-open').addEventListener('click', () => {
  try { optOut.checked = localStorage.getItem(OFF_KEY) === '1'; } catch { optOut.disabled = true; }
  document.getElementById('purge-result').hidden = true;
  settings.showModal();
});
optOut.addEventListener('change', () => {
  try {
    if (optOut.checked) localStorage.setItem(OFF_KEY, '1');
    else localStorage.removeItem(OFF_KEY);
  } catch { /* storage blocked; nothing is being counted anyway */ }
});
document.getElementById('signout').addEventListener('click', () => {
  settings.close();
  if (token()) signOut();
  else location.href = '../account/';
});
document.getElementById('purge').addEventListener('click', async () => {
  const days = Number(document.getElementById('purge-age').value);
  const result = document.getElementById('purge-result');
  if (!confirm(`Permanently delete every page view and event older than ${days} days? This cannot be undone.`)) return;
  try {
    const out = await api('purge', { before: Date.now() - days * DAY }, { method: 'POST' });
    result.textContent = `Deleted ${num(out.views)} page views and ${num(out.events)} events.`;
    result.className = 'small';
    state.report = null;
  } catch (err) {
    result.textContent = err.message;
    result.className = 'small error';
  }
  result.hidden = false;
});

/**
 * In with the token this browser kept, or with the session of an account
 * `worker/auth.js` calls an admin; the token form only when neither is accepted.
 */
async function enter() {
  try {
    await api('live');
  } catch (err) {
    if (err instanceof Unauthorized) return signOut(token() ? 'That token was not accepted.' : '');
  }
  start();
}

enter();
